import { useCallback, useEffect, useRef, useState, type JSX, type ReactNode } from "react";
import { cn } from "./lib/utils";

export interface ScrollShadowProps {
  children: ReactNode;
  className?: string;
}

/**
 * Vertical scroll container that fades in a subtle inner shadow at the top
 * and/or bottom whenever there is more content out of view in that direction.
 */
export function ScrollShadow({ children, className }: ScrollShadowProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);

  const update = useCallback((): void => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const max = element.scrollHeight - element.clientHeight;
    setAtTop(element.scrollTop <= 1);
    setAtBottom(element.scrollTop >= max - 1);
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    for (const child of Array.from(element.children)) {
      observer.observe(child);
    }

    return () => {
      observer.disconnect();
    };
  }, [update]);

  return (
    <div className={cn("relative min-h-0", className)}>
      <div
        ref={ref}
        data-platform-sidebar-scroll=""
        onScroll={update}
        className="h-full min-h-0 overflow-y-auto overflow-x-hidden"
      >
        {children}
      </div>
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-sidebar-border to-transparent transition-opacity duration-200",
          atTop ? "opacity-0" : "opacity-100"
        )}
      />
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-sidebar-border to-transparent transition-opacity duration-200",
          atBottom ? "opacity-0" : "opacity-100"
        )}
      />
    </div>
  );
}
